import React, { useState, useEffect, useCallback } from "react";
import { apiCall } from "../main/ApiCall";
import { ControlType, AuditScopePopupProps, ToastMessage, createToastMessage } from "../interFaces/interfaces";
import ToastPopup from "./ToastPopup";
const AuditScopePopup: React.FC<AuditScopePopupProps> = ({ isOpen, onClose, onSave, initialValue = "", }) => {
    const [controls, setControls] = useState<ControlType[]>([]);
    const [selectedControls, setSelectedControls] = useState<string[]>([]);
    const [customText, setCustomText] = useState<string>("");
    const [message, setMessage] = useState<ToastMessage | null>(null);
    const allSelected = controls.length > 0 && selectedControls.length === controls.length;
    const getPrefix = (controls: string[]) => {
        if (controls.length === 1) {
            const firstWord = controls[0].split(" ")[0];
            return firstWord + "_";
        }
        return "";
    };
    const fetchControls = useCallback(async () => {
        try {
            const response = await apiCall("get", "/api/enums/ControlsEnum");
            setControls(response);
        } catch (error) {
            console.error("Failed to fetch controls:", error);
        }
    }, []);
    const parseInitialValue = useCallback(
        (value: string, controlsList: ControlType[]) => {
            let foundControls: string[] = [];
            let remainingText = value;
            controlsList.forEach((control) => {
                const firstWord = control.value.split(" ")[0];
                const prefix = firstWord + "_";
                if (value.startsWith(prefix)) {
                    foundControls = [control.value];
                    remainingText = value.replace(prefix, "");
                }
            });
            setSelectedControls(foundControls);
            setCustomText(remainingText);
        },
        []
    );
    useEffect(() => {
        if (isOpen) fetchControls();
    }, [isOpen, fetchControls]);
    useEffect(() => {
        if (isOpen && controls.length > 0) {
            if (initialValue) {
                parseInitialValue(initialValue, controls);
            } else {
                setSelectedControls([]);
                setCustomText("");
            }
        }
    }, [isOpen, initialValue, controls, parseInitialValue]);
    const handleControlToggle = (controlValue: string) => {
        let updated: string[];
        if (selectedControls.includes(controlValue)) {
            updated = selectedControls.filter((c) => c !== controlValue);
        } else {
            updated = [...selectedControls, controlValue];
        }
        setSelectedControls(updated);
    };
    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedControls([]);
        } else {
            setSelectedControls(controls.map((c) => c.value));
        }
    };
    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (selectedControls.length === 0) return;
        let value = e.target.value;
        const prefix = getPrefix(selectedControls);
        if (selectedControls.length === 1) {
            if (prefix && !value.startsWith(prefix)) {
                setMessage(createToastMessage("Prefix cannot be removed", "warning", 3000));
                return;
            }
        }
        if (value.length <= 60) {
            const textWithoutPrefix = selectedControls.length === 1 && prefix ? value.slice(prefix.length) : value;
            setCustomText(textWithoutPrefix);
        }
    };
    const prefix = getPrefix(selectedControls);
    const finalValue = prefix + customText;
    const handleSave = () => {
        if (finalValue.trim()) {
            onSave(finalValue, selectedControls);
            onClose();
        }
    };
    if (!isOpen) return null;
    return (
        <div className="audit-scope-overlay" onClick={onClose}>
            <div className="audit-scope-modal" onClick={(e) => e.stopPropagation()} >
                <div className="audit-scope-header">
                    <h2 className="audit-scope-title">Select Audit Scope</h2>
                    <div className="textbox-section">
                        <input type="text" placeholder={selectedControls.length === 0 ? "Select at least one control" : selectedControls.length === 1 ? "Enter scope..." : "Enter scope (no prefix)"} value={prefix + customText} onChange={handleTextChange} readOnly={selectedControls.length === 0} />
                    </div>
                </div>
                <div className="audit-scope-body">
                    <h3 className="section-title">Select Scope</h3>
                    <div className="control-item">
                        <label>
                            <input type="checkbox" checked={allSelected} onChange={handleSelectAll} />
                            <strong className="control-select">Select All</strong>
                        </label>
                    </div>
                    <div className="control-list">
                        {controls.map((control) => (
                            <label key={control.value} className="control-item">
                                <input type="checkbox" checked={selectedControls.includes(control.value)} onChange={() => handleControlToggle(control.value)} />
                                <span className="control-text">
                                    <strong>{control.value} - ({control.description})</strong>
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
                <div className="buttons">
                    <button className="cancelbutton" onClick={onClose}>Cancel</button>
                    <button className="savebutton" onClick={handleSave}>Save</button>
                </div>
            </div>
            {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
        </div>
    );
};
export default AuditScopePopup;